import React, { useMemo, useState, useEffect } from 'react';
import { useFormat } from '../../../i18n/format.js';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { X, Car, Shield, CheckCircle, Calendar, FileText } from 'lucide-react';
import { apiRequest } from '../../../api/core.js';
import { resolveCoverageLevel } from '../../../utils/insuranceStatus.js';

// Les libelles de couverture sont resolus par t('insurance.coverageShort.<code>').

const COVERAGE_COLORS = {
  tous_risques: 'bg-green-100 text-green-700 border-green-200',
  tiers_plus: 'bg-blue-100 text-blue-700 border-blue-200',
  tiers_simple: 'bg-gray-100 text-gray-700 border-gray-200',
  standard: 'bg-purple-100 text-purple-700 border-purple-200',
};

// Fallback guarantees — used only if DB lookup returns empty.
// Codes traduits au rendu par t('insurance.guarantees.<code>') ; les noms venant
// de la base sont affiches tels quels (ils sont deja dans la langue de reference).
const FALLBACK_GUARANTEES = {
  tous_risques: ['mandatoryLiability', 'theft', 'fire', 'glassBreakage', 'collisionDamage', 'naturalDisasters'],
  tiers_plus: ['mandatoryLiability', 'theft', 'fire', 'glassBreakage'],
  tiers_simple: ['mandatoryLiability'],
  standard: ['mandatoryLiability', 'theft', 'fire', 'glassBreakage'],
};

function VehicleInsuranceDetailModal({ vehicle, vehicleInsurances, contracts, coverageModels = [], onClose }) {
  const { t, fmt } = useFormat();
  // Un vehicule appartient a UNE filiale : sa devise vaut pour toutes les lignes.
  const { money } = useOrgCurrency();
  const formatCurrency = (amount) => money(amount || 0, vehicle);

  // DB-resolved guarantees: { [coverageLevel]: string[] }
  const [dbGuarantees, setDbGuarantees] = useState({});

  useEffect(() => {
    const loadGuarantees = async () => {
      try {
        // Load coverage models, junction table, and guarantee templates in parallel
        const [modelsRes, junctionsRes, templatesRes] = await Promise.all([
          apiRequest('/coverage_model'),
          apiRequest('/coverage_model_guarantee'),
          apiRequest('/guarantee_template'),
        ]);

        const models = modelsRes.data || [];
        const junctions = junctionsRes.data || [];
        const templates = templatesRes.data || [];

        // Build a map: level → guarantee names
        const levelToGuarantees = {};
        for (const model of models) {
          const modelJunctions = junctions.filter(
            j => j.coverage_model_id === model.id && j.inclusion_type !== 'excluded'
          );
          const names = modelJunctions
            .map(j => templates.find(t => t.id === j.guarantee_id))
            .filter(Boolean)
            .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
            .map(t => t.name);

          if (names.length > 0) {
            levelToGuarantees[model.level] = names;
            // Also map by code for flexibility
            levelToGuarantees[model.code] = names;
          }
        }

        setDbGuarantees(levelToGuarantees);
      } catch (err) {
        console.error('Failed to load coverage model guarantees:', err);
        // Will use fallback
      }
    };

    loadGuarantees();
  }, []);

  const getGuarantees = (coverageKey) => {
    // Prefer DB-resolved guarantees, fall back to hardcoded
    if (dbGuarantees[coverageKey]) return dbGuarantees[coverageKey];
    const codes = FALLBACK_GUARANTEES[coverageKey] || FALLBACK_GUARANTEES.standard;
    return codes.map(code => t(`insurance.guarantees.${code}`, { defaultValue: code }));
  };

  const insuranceRecords = useMemo(() => {
    return vehicleInsurances
      .filter(vi => vi.vehicle_id === vehicle.id)
      .map(vi => {
        const contract = contracts.find(c => c.id === vi.contract_id);
        return { ...vi, contract };
      })
      .sort((a, b) => {
        const aActive = a.contract?.status === 'active' ? 0 : 1;
        const bActive = b.contract?.status === 'active' ? 0 : 1;
        if (aActive !== bActive) return aActive - bActive;
        return new Date(b.assigned_at || 0) - new Date(a.assigned_at || 0);
      });
  }, [vehicle, vehicleInsurances, contracts]);

  // Meme vehicule => meme devise, l'addition est licite ici.
  const totalPremium = insuranceRecords.reduce((sum, vi) => sum + (Number(vi.premium_amount) || 0), 0);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[700px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Car className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-lg font-semibold text-white">
                {vehicle.license_plate}
              </h2>
              <p className="text-blue-100 text-sm">
                {vehicle.make} {vehicle.model} — {t('insurance.vehicleDetailModal.coverageCount', { count: insuranceRecords.length })}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Summary */}
          {insuranceRecords.length > 1 && (
            <div className="bg-blue-50 rounded-lg p-3 flex items-center justify-between text-sm border border-blue-100">
              <span className="text-blue-700">
                {t('insurance.vehicleDetailModal.activeCoverages', { n: insuranceRecords.length })}
              </span>
              <span className="text-blue-900 font-semibold">
                {t('insurance.vehicleDetailModal.totalPremium', { amount: formatCurrency(totalPremium) })}
              </span>
            </div>
          )}

          {/* Insurance Cards */}
          {insuranceRecords.map((record) => {
            const coverageKey = resolveCoverageLevel(record, coverageModels) || 'standard';
            const coverageLabel = t(`insurance.coverageShort.${coverageKey}`, { defaultValue: coverageKey });
            const colorClass = COVERAGE_COLORS[coverageKey] || COVERAGE_COLORS.standard;
            const guarantees = getGuarantees(coverageKey);
            const contract = record.contract;
            const isActive = contract?.status === 'active';
            const daysLeft = contract?.expiry_date
              ? Math.ceil((new Date(contract.expiry_date) - new Date()) / (1000 * 60 * 60 * 24))
              : null;

            return (
              <div
                key={record.id}
                className={`border rounded-lg overflow-hidden ${isActive ? 'border-gray-200' : 'border-gray-100 opacity-70'}`}
              >
                {/* Card Header */}
                <div className="px-5 py-3 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className="w-4 h-4 text-blue-500" />
                    <div>
                      <span className="font-medium text-gray-900">{contract?.policy_number || '—'}</span>
                      <span className="text-gray-400 mx-2">·</span>
                      <span className="text-sm text-gray-600">{contract?.insurer_name || '—'}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${colorClass}`}>
                    {coverageLabel}
                  </span>
                </div>

                {/* Card Body */}
                <div className="px-5 py-4 space-y-4">
                  {/* Key info row */}
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs mb-1">{t('insurance.vehicleDetailModal.annualPremium')}</p>
                      <p className="font-semibold text-gray-900">
                        {record.premium_amount ? formatCurrency(record.premium_amount) : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs mb-1">{t('insurance.vehicleDetailModal.deductible')}</p>
                      <p className="font-semibold text-gray-900">
                        {record.deductible_amount ? formatCurrency(record.deductible_amount) : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs mb-1">{t('insurance.vehicleDetailModal.contractExpiry')}</p>
                      <p className={`font-semibold ${daysLeft !== null && daysLeft <= 90 ? 'text-orange-600' : 'text-gray-900'}`}>
                        {contract?.expiry_date ? fmt.date(contract.expiry_date) : '—'}
                      </p>
                      {daysLeft !== null && daysLeft <= 90 && (
                        <p className="text-xs text-orange-500">{t('insurance.daysLeft', { n: daysLeft })}</p>
                      )}
                    </div>
                  </div>

                  {/* Dates row */}
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      {t('insurance.vehicleDetailModal.effectiveFrom', { date: record.assigned_at ? fmt.date(record.assigned_at) : '—' })}
                    </span>
                    {contract?.product_name && (
                      <span className="flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-gray-400" />
                        {contract.product_name}
                      </span>
                    )}
                  </div>

                  {/* Guarantees */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">{t('insurance.vehicleDetailModal.includedGuarantees')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {guarantees.map((g, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-50 border border-gray-200 rounded text-gray-700"
                        >
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          {g}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {insuranceRecords.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>{t('insurance.vehicleDetailModal.empty')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VehicleInsuranceDetailModal;
